import React, { useEffect, useState, useContext, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import notificationIcon from "../../images/notification.png";
import emptyImage from "../../images/empty.jpg";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import { ChatContext } from "../../context/contexts";
import {
  convertTo12HourPlusDateFormat,
  trimWord,
  formatBoldText,
  buildConversationPath,
} from "../../util/helperFunctions";
import bellNotifSound from "../../sounds/bell-notif.mp3";
import { useNavigate } from "react-router-dom";

function NotificationBell({ addtlClass }) {
  const Chat = useContext(ChatContext);
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationList, setNotificationList] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loadingFirstPage, setLoadingFirstPage] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const FINAL_RESOURCES_URL = `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/`;

  const mergeUniqueById = (prev, next) => {
    const map = new Map();
    for (const n of prev) map.set(n.id, n);
    for (const n of next) map.set(n.id, n);
    return Array.from(map.values());
  };

  const loadPage = useCallback(
    async (pageToLoad, mode = "append") => {
      // mode: "replace" | "append"
      const resp = await ApiService.users.getAllNotificationsv2(
        pageToLoad,
        limit,
      );
      const { success, notifications: notificationListResp } = resp;

      if (!success) {
        window.location.reload();
        return;
      }

      const {
        count,
        unread_count,
        notifications: incoming,
      } = notificationListResp;

      setTotalCount(count || 0);
      setNotificationCount(unread_count || 0);

      if (mode === "replace") {
        setNotificationList(incoming || []);
      } else {
        setNotificationList((prev) => mergeUniqueById(prev, incoming || []));
      }

      // hasMore based on count and page/limit
      const more = pageToLoad * limit < (count || 0);
      setHasMore(more);
    },
    [limit],
  );

  // Refresh badge counts without overwriting list (nice UX)
  const refreshCountsOnly = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const resp = await ApiService.users.getAllNotificationsv2(1, limit);
      const { success, notifications: notificationListResp } = resp;
      if (!success) return;

      const { count, unread_count } = notificationListResp;
      setTotalCount(count || 0);
      setNotificationCount(unread_count || 0);
    } finally {
      // small delay prevents flicker
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, [limit]);

  // Play sound when unread_count changes
  useEffect(() => {
    const __notif = new Audio(bellNotifSound);
    if (notificationCount > 0) {
      __notif.play().catch(() => {});
    }
  }, [notificationCount]);

  // When dropdown opens: fetch first page
  useEffect(() => {
    if (!showNotifications) return;

    (async () => {
      setLoadingFirstPage(true);
      setPage(1);
      await loadPage(1, "replace");
      setLoadingFirstPage(false);
    })();
  }, [showNotifications, loadPage]);

  // Poll unread badge every 2 minutes (does not clobber list)
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshCountsOnly();
    }, 2 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [refreshCountsOnly]);

  const fetchMoreNotifications = async () => {
    if (isFetchingMore) return;
    setIsFetchingMore(true);

    try {
      const nextPage = page + 1;
      setPage(nextPage);
      await loadPage(nextPage, "append");
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleBellClick = () => {
    setShowNotifications((prev) => !prev);
  };

  const handleReadDeleteClick = async (notification_id, action) => {
    const response = await ApiService.notifications.readDeleteNotification(
      { action },
      notification_id,
    );
    const { success } = response;
    if (!success) return showSwal(common.errors.UPDATE_NOTIFICATION_ERROR);

    // Optimistic UI updates:
    if (action === "delete") {
      setNotificationList((prev) =>
        prev.filter((n) => n.id !== notification_id),
      );
      setTotalCount((prev) => Math.max(0, prev - 1));
      refreshCountsOnly();
    }

    if (action === "read") {
      setNotificationList((prev) =>
        prev.map((n) =>
          n.id === notification_id ? { ...n, is_read: true } : n,
        ),
      );
      refreshCountsOnly();
    }
  };

  const handleNotificationClick = async (
    notification_id,
    conversation_id,
    message_id,
    reply_to_thread_message_id,
    notification_type,
    request_notification_form_request_id,
  ) => {
    await handleReadDeleteClick(notification_id, "read");

    if (notification_type === "Removed") {
      return;
    } else if (notification_type === "Mute") {
      navigate(buildConversationPath(conversation_id));
    } else if (
      request_notification_form_request_id !== null &&
      request_notification_form_request_id !== ""
    ) {
      window.location.href = `/requests?p=view-request-detail&t=ePR&r=${request_notification_form_request_id}`;
    } else {
      const msgIndex = await ApiService.message.getMessagePosition(
        conversation_id,
        message_id,
      );
      const { success, response } = msgIndex;
      const { index, query } = response;

      if (!success) return showSwal(common.errors.CLICK_NOTIFICATION_ERROR);

      if (
        reply_to_thread_message_id !== "" &&
        reply_to_thread_message_id !== null
      ) {
        navigate(
          buildConversationPath(conversation_id, {
            q: message_id,
            i: index,
            _q: query,
            _t: reply_to_thread_message_id,
            _tr: true,
          }),
        );
      } else {
        navigate(
          buildConversationPath(conversation_id, {
            q: message_id,
            i: index,
            _q: query,
          }),
        );
      }
    }
  };

  return (
    <div className={`profile-container notification-container ${addtlClass}`}>
      <img
        src={notificationIcon}
        alt="Notification Icon"
        className="notification-icon"
        onClick={handleBellClick}
      />

      {notificationCount > 0 && (
        <div className="notification-count-container">
          <div className="notification-count">{notificationCount}</div>
        </div>
      )}

      {showNotifications && (
        <div className="notification-list" id="notificationScrollDiv">
          {/* Subtle “alive” syncing bar */}
          <div className={`notif-top-status ${isRefreshing ? "show" : ""}`}>
            Syncing…
          </div>

          {loadingFirstPage ? (
            <NotificationSkeletonList rows={7} />
          ) : notificationList.length > 0 ? (
            <InfiniteScroll
              dataLength={notificationList.length}
              next={fetchMoreNotifications}
              hasMore={hasMore}
              scrollableTarget="notificationScrollDiv"
              loader={
                <div className="notif-loadmore">
                  <div className="notif-loadmore-spinner" />
                  <span>Loading more notifications…</span>
                </div>
              }
              endMessage={
                <div className="notif-end">
                  <span>You're all caught up ✅</span>
                </div>
              }
            >
              {notificationList.map((notification) => (
                <div
                  className={`notification-item ${
                    notification.is_read ? "" : "notification-item-unread"
                  }`}
                  key={notification.id}
                >
                  <div
                    className="notification--item"
                    onClick={() => {
                      if (notification.notification_type === "removed") return;

                      handleNotificationClick(
                        notification.id,
                        notification.conversation_id,
                        notification.message_id,
                        notification.reply_to_thread_message_id,
                        notification.notification_type,
                        notification.request_notification_form_request_id,
                      );
                    }}
                  >
                    <img
                      src={
                        notification.conversation_details?.image
                          ? `${FINAL_RESOURCES_URL}${notification.conversation_details.image}`
                          : emptyImage
                      }
                      alt="User"
                      className="notification-user-image"
                    />
                    <div>
                      <div className="notification-message">
                        {renderNotificationMessage(
                          notification.notification_type,
                          notification.message_details,
                          notification.notification_sender_details,
                          notification.conversation_details,
                        )}
                      </div>
                      <small>
                        {convertTo12HourPlusDateFormat(notification.createdAt)}
                      </small>
                    </div>
                  </div>

                  <div className="notification-actions">
                    {!notification.is_read && (
                      <div
                        className="notification-action-check notif-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadDeleteClick(notification.id, "read");
                        }}
                      >
                        <i
                          className="fa fa-check-square-o fa-2x"
                          aria-hidden="true"
                        />
                      </div>
                    )}

                    <div
                      className="notification-action-trash notif-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReadDeleteClick(notification.id, "delete");
                      }}
                    >
                      <i className="fa fa-trash-o fa-2x" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              ))}

            </InfiniteScroll>
          ) : (
            <div className="notification-item">
              <span>No notifications found.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===========================
   Skeleton Components (UX)
=========================== */
const NotificationSkeletonRow = () => (
  <div className="notif-skel-row">
    <div className="notif-skel-avatar skel" />
    <div className="notif-skel-body">
      <div className="notif-skel-line skel" />
      <div className="notif-skel-line skel short" />
    </div>
  </div>
);

const NotificationSkeletonList = ({ rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <NotificationSkeletonRow key={i} />
    ))}
  </>
);

const renderNotificationMessage = (
  notificationType,
  messageDetails,
  notifSender,
  conversationDetails,
) => {
  const notification_type = notificationType?.toLowerCase?.() || "";

  const senderName = notifSender?.display_name || "Someone";
  const messageContent = messageDetails?.content
    ? (trimWord(messageDetails.content, 15))
    : "";
  const conversationName = conversationDetails?.name || "this conversation";

  switch (notification_type) {
    case "reaction":
      return (
        <span>
          <b>{senderName}</b> reacted to your message{" "}
          {messageContent ? `'${messageContent}'` : "."}
        </span>
      );

    case "mention":
      return (
        <span>
          <b>{senderName}</b> mentioned you in a chat{" "}
          {messageDetails?.content ? (
            <>“{formatBoldText(trimWord(messageDetails.content, 15))}”</>
          ) : (
            "."
          )}
        </span>
      );

    case "thread mention":
      return (
        <span>
          <b>{senderName}</b> mentioned you in a thread reply{" "}
          {messageContent ? `'${messageContent}'` : "."}
        </span>
      );

    case "mute":
      return (
        <span>
          <b>{senderName}</b> muted you in the conversation '{conversationName}
          '.
        </span>
      );

    case "unmute":
      return (
        <span>
          <b>{senderName}</b> unmuted you in the conversation '
          {conversationName}'.
        </span>
      );

    case "removed":
      return (
        <span>
          <b>{senderName}</b> removed you from the conversation '
          {conversationName}'.
        </span>
      );

    case "new form request":
      return (
        <span>
          <b>{senderName}</b> submitted a form request that needs your approval.
        </span>
      );

    case "approve form request":
      return (
        <span>
          <b>{senderName}</b> approved a form request that now needs your
          approval.
        </span>
      );

    case "approved form request":
      return (
        <span>
          <b>{senderName}</b> approved your request. Your request is now{" "}
          <b>APPROVED</b>.
        </span>
      );

    case "denied form request":
      return (
        <span>
          <b>{senderName}</b> denied your form request.
        </span>
      );

    default:
      return (
        <span>UNSUPPORTED NOTIFICATION TYPE. PLEASE CONTACT SUPPORT.</span>
      );
  }
};

export default NotificationBell;
